
/**
 * UIManager — React bridge version.
 * All methods update shared React state via registerUIStateUpdater().
 * ApplicationController and AREngine are unchanged.
 */
import { ClientConfig } from '../config/clients.js'
import { CharacterConfig } from '../types/index.js'
import { getRouter } from '../utils/Router.js'

export interface UiState {
  screen: 'consent' | 'app' | 'error'
  activeTab: 'home' | 'explore' | 'scan' | 'shop' | 'profile'
  tabTapKey: number
  loadingProgress: number
  consentGiven: boolean
  showAboutPage: boolean
  showGuidance: boolean
  guidanceMessage: string
  showCameraWarning: boolean
  cameraWarningMessage: string
  showReplay: boolean
  showCharacterSelectBtn: boolean
  showCharacterSelector: boolean
  selectorCharacters: CharacterConfig[]
  isRecording: boolean
  showPatentPending: boolean
  showVideoPlayer: boolean
  videoUrl: string | null
  videoBlob: Blob | null
  showDiscoverPanel: boolean
  tikTokMeta: {
    animationId: string
    username: string
    description: string
    hashtags: string[]
    soundName: string
    avatarSrc: string
  }
  clients: ClientConfig[]
  activeClientId: string
  errorTitle: string
  errorMessage: string
  errorInstructions: string[]
}

export const defaultUiState: UiState = {
  screen: 'consent',
  activeTab: 'home',
  tabTapKey: 0,   
  loadingProgress: 0,
  consentGiven: false,
  showAboutPage: false,
  showGuidance: false,
  guidanceMessage: 'Move camera slowly over flat prints or shirts to start',
  showCameraWarning: false,
  cameraWarningMessage: '',
  showReplay: false,
  showCharacterSelectBtn: false,
  showCharacterSelector: false,
  selectorCharacters: [],
  isRecording: false,
  showPatentPending: false,
  showVideoPlayer: false,
  videoUrl: null,
  videoBlob: null,
  showDiscoverPanel: false,
  tikTokMeta: {
    animationId: 'default',
    username: '@rockar',
    description: 'Experience AR through your camera',
    hashtags: ['#AR', '#RockAR', '#AugmentedReality'],
    soundName: 'Original Sound — ROCK AR',
    avatarSrc: '/rockarlogo.png',
  },
  clients: [],
  activeClientId: 'roq',
  errorTitle: 'Error',
  errorMessage: 'An error occurred.',
  errorInstructions: [],
}

type SetFn = (fn: (prev: UiState) => UiState) => void
let _set: SetFn | null = null

export function registerUIStateUpdater(setFn: SetFn): void {
  _set = setFn
}

function upd(updates: Partial<UiState>): void {
  _set?.((prev) => ({ ...prev, ...updates }))
}

let _onClientSwitch: ((clientId: string) => void) | null = null
let _onCharacterSelect: ((character: CharacterConfig) => void) | null = null
let _characterSelectBtnClick: (() => void) | null = null

export function getOnClientSwitch() { return _onClientSwitch }
export function getOnCharacterSelect() { return _onCharacterSelect }
export function getCharacterSelectBtnClick() { return _characterSelectBtnClick }

export class UIManager {
  private consentGiven = false
  private loadingProgress = 0
  private isRecording = false
  private sliderClients: ClientConfig[] = []

  constructor(_container: HTMLElement) {
    this.setupEventListeners()
    this.setupRouting()
  }

  private setupEventListeners(): void {
    document.addEventListener('showGuidance', (event: Event) => {
      const e = event as CustomEvent
      upd({ showGuidance: true, guidanceMessage: e.detail?.message || 'Move camera slowly over flat prints or shirts to start' })
    })
    document.addEventListener('hideGuidance', () => { upd({ showGuidance: false }) })
    document.addEventListener('cameraFeedWarning', (event: Event) => {
      const e = event as CustomEvent
      upd({
        showCameraWarning: true,
        cameraWarningMessage: e.detail?.message || 'Camera is on but no live frames detected. Move to better light or refresh.'
      })
    })
    document.addEventListener('cameraFeedHealthy', () => {
      upd({ showCameraWarning: false, cameraWarningMessage: '' })
    })
    document.addEventListener('recordingComplete', (event: Event) => {
      const e = event as CustomEvent
      const { videoUrl, videoBlob } = e.detail || {}
      if (videoUrl) upd({ showVideoPlayer: true, videoUrl, videoBlob: videoBlob || null })
    })
    document.addEventListener('consentChanged', (event: Event) => {
      const e = event as CustomEvent
      //this.consentGiven = !!e.detail?.consentGiven
      //upd({ consentGiven: this.consentGiven })
      this.consentGiven = !!e.detail?.consentGiven

      upd({consentGiven: this.consentGiven})

      if (this.consentGiven && this.loadingProgress >= 100) {
        this.handleStartExperience()
      }
    })
  }

  private setupRouting(): void {
    getRouter().onRouteChange((event) => {
      if (event.newRoute === 'about') {
        upd({ showAboutPage: true, showPatentPending: false, showGuidance: false })
      } else if (event.newRoute === 'ar') {
        upd({ showAboutPage: false, showPatentPending: true })
      }
    })
  }

  isConsentGiven(): boolean { return this.consentGiven }
  showConsentScreen(): void { upd({ screen: 'consent' }) }
  //updateLoadingProgress(progress: number): void { upd({ loadingProgress: progress }) }
  updateLoadingProgress(progress: number): void {
    this.loadingProgress = Math.max(0, Math.min(100, progress))
    upd({ loadingProgress: this.loadingProgress })
    if (this.consentGiven && this.loadingProgress >= 100) {
      this.handleStartExperience()
    }
  }
  getLoadingProgress(): number {
    return this.loadingProgress
  }
  showScanningHint(state: 'aiming' | 'looking' | 'hold-steady'): void {
  const messages = {
      aiming: 'Aim your camera at the design to begin',
      looking: 'Scanning movement for the design…',
      'hold-steady': 'Hold steady — loading animation…'
    }

    this.showGuidanceText(messages[state])
  }
  showCameraErrorScreen(errorMessage?: string): void {
    this.showError(
      'Camera Access Required',
      errorMessage || 'Camera access is required for AR.',
      [
        'Refresh the page.',
        'Allow camera access.',
        'Point your camera at the ROQ logo.'
      ])
  }
  showCameraView(): void { upd({ screen: 'app', activeTab: 'home', showAboutPage: false, showPatentPending: true }) }
  showGuidanceText(message?: string): void { upd({ showGuidance: true, guidanceMessage: message || 'Move camera slowly over flat prints or shirts to start' }) }
  hideGuidanceText(): void { upd({ showGuidance: false }) }
  showROQCTAButton(): void {}
  hideROQCTAButton(): void {}
  showCTAButton(): void {}
  hideCTAButton(): void {}
  showTopLogo(): void {}
  hideTopLogo(): void {}
  showRockARText(): void {}
  hideRockARText(): void {}
  showBottomCTABanner(): void {}
  hideBottomCTABanner(): void {}
  showBottomNavBar(): void { upd({ showPatentPending: true }) }
  hideBottomNavBar(): void {}
  showPatentPendingText(): void { upd({ showPatentPending: true }) }
  hidePatentPendingText(): void { upd({ showPatentPending: false }) }
  showReplayButton(): void { upd({ showReplay: true }) }
  hideReplayButton(): void { upd({ showReplay: false }) }
  showSocialInteractionPanel(animationId = 'roq-ar-demo'): void {
    document.dispatchEvent(new CustomEvent('setAnimationId', { detail: { animationId } }))
  }
  hideSocialInteractionPanel(): void {}
  showAboutPage(): void { upd({ showAboutPage: true, showPatentPending: false, showGuidance: false }) }
  hideAboutPage(): void { upd({ showAboutPage: false }) }
  showPermissionsErrorScreen(): void {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      !/Chrome/.test(navigator.userAgent) &&
      !/CriOS/.test(navigator.userAgent)

    this.showPermissionError(isIOS, isSafari)
  }
  setOnClientSwitch(clients: ClientConfig[], activeClientId: string, onSwitch: (clientId: string) => void): void {
    this.sliderClients = clients
    _onClientSwitch = onSwitch
    const active = clients.find((c) => c.clientId === activeClientId)
    const tikTokMeta = active ? {
      animationId: activeClientId,
      username: `@${active.clientName.toLowerCase().replace(/\s+/g, '')}`,
      description: (active as any).experienceItem?.description || (active as any).experienceConfig?.aboutText?.split('.')[0] || '',
      hashtags: ['#AR', `#${active.clientName.replace(/\s+/g, '')}`, '#RockAR'],
      soundName: `${active.clientName} — Original Sound`,
      avatarSrc: (active as any).experienceItem?.thumbnail || '/rockarlogo.png',
    } : undefined
    upd({ clients, activeClientId, ...(tikTokMeta ? { tikTokMeta } : {}) })
  }

  setOnCharacterSelect(onSelect: (character: CharacterConfig) => void): void { _onCharacterSelect = onSelect }
  showCharacterSelector(characters: CharacterConfig[]): void { upd({ showCharacterSelector: true, selectorCharacters: characters }) }
  hideCharacterSelector(): void { upd({ showCharacterSelector: false }) }
  showCharacterSelectButton(onClick: () => void): void { _characterSelectBtnClick = onClick; upd({ showCharacterSelectBtn: true }) }
  hideCharacterSelectButton(): void { _characterSelectBtnClick = null; upd({ showCharacterSelectBtn: false }) }
  setOnDiscover(_cb: () => void): void {}
  //handleDiscoverTap(): void { upd({ showDiscoverPanel: true }) }
  handleDiscoverTap(): void {

    if (!this.sliderClients.length || !_onClientSwitch) {
      upd({ showDiscoverPanel: true })
      return
    }

    upd({showDiscoverPanel: true,clients: this.sliderClients})
  }
  showDiscoverPanel(): void { upd({ showDiscoverPanel: true }) }
  hideDiscoverPanel(): void { upd({ showDiscoverPanel: false }) }
  showVideoPlayer(videoUrl: string, videoBlob?: Blob): void { upd({ showVideoPlayer: true, videoUrl, videoBlob: videoBlob || null }) }
  hideVideoPlayer(): void { upd({ showVideoPlayer: false, videoUrl: null, videoBlob: null }) }
  //updateRecordingState(isRecording: boolean): void { upd({ isRecording }) }
  updateRecordingState(isRecording: boolean): void {
    this.isRecording = isRecording
    upd({ isRecording })
  }
  setRecordedVideo(_blob: Blob | null): void {}
  showScanTopCorners(): void {}
  hideScanTopCorners(): void {}
  clearCurrentOverlay(): void {}

  private showError(title: string, message: string, instructions: string[]): void {
    upd({ screen: 'error', errorTitle: title, errorMessage: message, errorInstructions: instructions })
  }
  private handleRecordToggle(isRecording: boolean): void {
    this.isRecording = isRecording
    upd({ isRecording })

    document.dispatchEvent(new CustomEvent('toggleRecording', {detail: { isRecording }}))
  }

  private handleStartExperience(): void {
    document.dispatchEvent(
      new CustomEvent('startExperience', {
        detail: {consentGiven: true, timestamp: Date.now()}}))
  }
  showPermissionError(isIOS: boolean, isSafari: boolean): void {
    const instructions = isIOS && isSafari
      ? ['Enable Location: Settings > Privacy > Location Services > Safari > Allow', 'Tap refresh', 'Tap "Allow" for camera', 'Tap "Allow" for location']
      : ['Click refresh', 'Click "Allow" for camera access', 'Click "Allow" for location access']
    this.showError('All Permissions Required', 'Camera and location permissions are required.', instructions)
  }

  showBrowserCompatibilityError(result: { issues: string[]; browserInfo: { name: string; version: string } }): void {
    this.showError('Browser Not Supported', `${result.browserInfo.name} ${result.browserInfo.version} is not supported.`, [...result.issues, 'Please use Chrome 80+, Firefox 75+, Safari 14+, or Edge 85+'])
  }

  showARInitializationError(errorMessage: string): void {
    this.showError('AR Initialization Failed', errorMessage, ["Use a supported browser", "Enable WebGL", "Close apps using the camera", "Refresh the page"])
  }

  showCameraPermissionError(isIOS = false, isSafari = false): void { this.showPermissionError(isIOS, isSafari) }
  showLocationPermissionError(isIOS = false, isSafari = false): void { this.showPermissionError(isIOS, isSafari) }

}
